import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import { ConfirmationService, LazyLoadEvent, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ProgressData } from 'src/app/modules/shared/dtos/progress-data';
import { ResponseDto } from 'src/app/modules/shared/dtos/response.dto';
import { ResponseModel } from 'src/app/modules/shared/models/reponse.model';
import { ProgressIndicatingService } from 'src/app/modules/shared/services/progress-indicating.service';
import { AuditTrailService } from '../../audit-trail/services/audit-trail.service';
import { TestActivationRequestDTO } from '../dtos/request-dto/test-activation-request';
import { ContractResponse } from '../dtos/response-dto/contract-response';
import { CustomerActivationResponse } from '../dtos/response-dto/customer-activation-response';
import { TestActivationResponse } from '../dtos/response-dto/test-activation.response';
import { FilterContract } from '../interfaces/filter-contract';
import { ReportService } from '../services/reports.service';
import { ColumnNamesUtilityService } from '../utilities/column-names-utility.service';

@Component({
    selector: 'app-reports-component',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {
  private progress$ = new Subject<ProgressData>();
  public partnerDetails: Array<{}> = [];
  public maxScheduledEndDate : Date;
  public maxScheduledStartDate : Date
  public showMessage : string = '';
  public first = 0;
  public rows = 10;
  public pageNumber: number = 1;
  public totalRecords: number;
  public sortField : string = '';
  public sortOrder : string = '';
  public reportsList : Array<{}> = [];
  public norecordsFound : boolean = false;
  public testActivationReport : Array<object> = [];
  public isDataAvailable: boolean = false;
  public customerSupportActivation : Array<{}> = [];
  public contractReport : Array<{}> = [];
  public activationcolumn : Array<{}> = [];
  public customerColumn: Array<{}> = [];
  public contractColumn: Array<{}> = [];
  public previousData : FilterContract;
  public renewalStatus: string;

  public constructor(private _auditTrailService : AuditTrailService,
      private progressService: ProgressIndicatingService,
      private messageService: MessageService,
     private _reportService : ReportService,
      private _columnNamesUtilityService : ColumnNamesUtilityService,
      private confirmationService: ConfirmationService) { }

    public filterDataForm = new FormGroup({
        reports: new FormControl('', [
            Validators.required
        ]),
        partnerId: new FormControl(''),
        startDate: new FormControl('', Validators.required),
        endDate: new FormControl('')
    });

    public ngOnInit(): void {
        this.getPartners();
        this.activationcolumn = this._columnNamesUtilityService.getTestActivationColumnName();
        this.customerColumn = this._columnNamesUtilityService.customerActivationColumnName();
        this.contractColumn = this._columnNamesUtilityService.getContractColumnName();
        this.reportsList = [
            {
                name: 'Contract', value: 'contract'
            },
            {
                name: 'Test Activation', value: 'testActivation'
            },
            {
                name: 'Customer Support Activation', value: 'customerSupportActivation'
            }
        ];
        const fiveMinutesIntoFuture = new Date();
        fiveMinutesIntoFuture.setMinutes(fiveMinutesIntoFuture.getMinutes());
        const fiveMinutesIntoFutureUtc = new Date(fiveMinutesIntoFuture.getTime() + fiveMinutesIntoFuture.getTimezoneOffset() * 60 * 1000);
        this.maxScheduledStartDate = fiveMinutesIntoFutureUtc;
        this.maxScheduledEndDate = fiveMinutesIntoFutureUtc;
        this.filterDataForm.get('endDate')?.valueChanges.subscribe((data: Date) => {
            if (data === null) {
                this.showMessage = '';
            }
        });
        this.filterDataForm.controls.partnerId.disable();
    }

    public getPartners() : void {
        this.progressService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Partner Details',
                details: 'Please wait...',
                done: false
            }
        });
        this._auditTrailService.getPartners().pipe(
            filter(response => new ResponseModel(response).successful),
            map(res => res.partnerCUModel)
        ).subscribe(data => {
            this.partnerDetails = [];
            this.partnerDetails.push({ partnerId: 'ALL', value: 'ALL' });
            data.forEach(x => this.partnerDetails.push({ partnerId: x.partnerId, value: x.partnerId }));
            this.sendProgressDoneSignal();
        },
        (response: unknown): void => {
            this.sendProgressDoneSignal();
            const res = response as { error: ResponseDto };
            this.messageService.add({ severity: 'error', summary: 'Fail', detail: new ResponseModel(res.error).message });
        });
    }

    public clearSortFields() : void {
        this.sortField = '';
        this.sortOrder = '';
    }

    public clearData() : void {
        this.isDataAvailable = false;
        this.norecordsFound = false;
    }

    public onChangePartner(event:{}) : void {
        this.clearData();
    }

    public displayLog() : void {
        this.clearSortFields();
        this.clearData();
        if (this.filterDataForm.value.partnerId === 'ALL') {
            this.filterDataForm.patchValue({
                partnerId: ''
            });
        }
        const testActivationRequestBody : TestActivationRequestDTO = {
            sortField: '',
            sortOrder: '',
            page: this.pageNumber,
            row: this.rows,
            filter: {
                startDate: moment(this.filterDataForm.value.startDate).format('YYYY-MM-DD'),
                endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value.endDate).format('YYYY-MM-DD') : '',
                eventType: this.filterDataForm.value.eventType?.name,
                eventAction: this.filterDataForm.value.action?.name,
                status: this.filterDataForm.value.status?.value,
                partnerId: this.filterDataForm.value.partnerId ? this.filterDataForm.value.partnerId : ''

            }
        };
        this.postTestActivation(testActivationRequestBody, false);
    }

    public submitData() : void {
        if (this.filterDataForm.value.partnerId === 'ALL') {
            this.filterDataForm.patchValue({
                partnerId: ''
            });
        }
        const testActivationRequestBody : TestActivationRequestDTO = {
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            page: this.pageNumber,
            row: this.rows,
            filter: {
                startDate: moment(this.filterDataForm.value.startDate).format('YYYY-MM-DD'),
                endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value.endDate).format('YYYY-MM-DD') : '',
                eventType: this.filterDataForm.value.eventType?.name,
                eventAction: this.filterDataForm.value.action?.name,
                status: this.filterDataForm.value.status?.value,
                partnerId: this.filterDataForm.value.partnerId ? this.filterDataForm.value.partnerId : ''

            }
        };
        this.postTestActivation(testActivationRequestBody, false);
    }

    public postTestActivation(request: TestActivationRequestDTO, isExport: boolean) : void {
        this.previousData = {};
        if (isExport === true) {
            this.progressService.show({
                progress$: this.progress$.asObservable(),
                initial: {
                    header: 'Exporting Data in progress',
                    details: 'Please wait ...',
                    done: false
                }
            });
        } else {
            this.progressService.show({
                progress$: this.progress$.asObservable(),
                initial: {
                    header: 'Loading Report Details',
                    details: 'Please wait ...',
                    done: false
                }
            });
        }

        if (this.filterDataForm.value.reports.name === 'Test Activation') {
            this.norecordsFound = false;
            this._reportService.postReportTestActivation(request).pipe(
                filter((response: TestActivationResponse) => response.responseCode === 0 || response.responseCode !== 0))
                .subscribe((data: TestActivationResponse) => {
                    this.previousData = JSON.parse(JSON.stringify(request.filter));
                    if (data.results?.data !== undefined) {
                        /** Export as Excel starts */
                        if (isExport === true) {
                            import('xlsx').then(xlsx => {
                                const worksheet = xlsx.utils.json_to_sheet((data.results.data));
                                const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
                                const excelBuffer: unknown = xlsx.write(workbook, {
                                    bookType: 'xlsx',
                                    type: 'array'
                                });
                                this.saveAsExcelFile(excelBuffer, 'Reports');
                                this.sendProgressDoneSignal();
                            });
                        } else {
                            this.testActivationReport = [];
                            this.sendProgressDoneSignal();
                            this.isDataAvailable = true;
                            data.results.data.forEach(x => {
                                while (x.beams.includes('<->')) {
                                    x.beams = x.beams.replace('<->', ', ');
                                }
                            });
                            this.testActivationReport = data.results.data.map((item, index) => ({ index, ...item }));
                            this.totalRecords = data.results.total;
                        }
                        /** Export as Excel ends */
                    } else {
                        this.testActivationReport = [];
                        this.norecordsFound = true;
                        this.sendProgressDoneSignal();
                    }
                },
                error => {
                    this.apiResponsePopup(error.error.responseMessage!, error.responseCode!);
                    this.sendProgressDoneSignal();
                    this.previousData.endDate = '';
                    this.previousData.partnerId = '';
                    this.filterDataForm.patchValue({
                        endDate: '',
                        partnerId: ''
                    });
                });
        } else if (this.filterDataForm.value.reports.name === 'Contract') {
            this.norecordsFound = false;
            this._reportService.postReportContract(request).pipe(
                filter((response: ContractResponse) => response.responseCode === 0 || response.responseCode !== 0))
                .subscribe((data: ContractResponse) => {
                    this.previousData = JSON.parse(JSON.stringify(request.filter));
                    if (data.results?.data !== undefined) {
                        /** Export as Excel starts */
                        if (isExport === true) {
                            import('xlsx').then(xlsx => {
                                const worksheet = xlsx.utils.json_to_sheet((data.results.data));
                                const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
                                const excelBuffer: unknown = xlsx.write(workbook, {
                                    bookType: 'xlsx',
                                    type: 'array'
                                });
                                this.saveAsExcelFile(excelBuffer, 'Reports');
                                this.sendProgressDoneSignal();
                            });
                        } else {
                            this.contractReport = [];
                            this.sendProgressDoneSignal();
                            this.isDataAvailable = true;
                            data.results.data.forEach(x => {
                                while (x.beams.includes('<->')) {
                                    x.beams = x.beams.replace('<->', ', ');
                                }
                                if (x.isRenewal === true) {
                                    x.isRenewal = 'Yes';
                                } else {
                                    x.isRenewal = 'No';
                                }
                                // console.log(x.activationCount);

                                // if (x.activationCount > 1) {
                                //     this.activationCountStatus = 'No';
                                // } else {
                                //     this.activationCountStatus = 'Yes';
                                // }
                            });
                            this.contractReport = data.results.data.map((item, index) => ({ index, ...item }));
                            // this.contractReport = this.contractReport.map(obj => ({ ...obj }));

                            this.totalRecords = data.results.total;
                        }
                        /** Export as Excel ends */
                    } else {
                        this.contractReport = [];
                        this.norecordsFound = true;
                        this.sendProgressDoneSignal();
                    }
                },
                error => {
                    this.apiResponsePopup(error.error.responseMessage!, error.responseCode!);
                    this.sendProgressDoneSignal();
                    this.previousData.endDate = '';
                    this.previousData.partnerId = '';
                    this.filterDataForm.patchValue({
                        endDate: '',
                        partnerId: ''
                    });
                });
        } else if (this.filterDataForm.value.reports.name === 'Customer Support Activation') {
            this.norecordsFound = false;
            this._reportService.postReportCustomerService(request).pipe(
                filter((response: CustomerActivationResponse) => response.responseCode === 0 || response.responseCode !== 0))
                .subscribe((data: CustomerActivationResponse) => {
                    this.previousData = JSON.parse(JSON.stringify(request.filter));
                    if (data.results?.data !== undefined) {
                        /** Export as Excel starts */
                        if (isExport === true) {
                            import('xlsx').then(xlsx => {
                                const worksheet = xlsx.utils.json_to_sheet((data.results.data));
                                const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
                                const excelBuffer: unknown = xlsx.write(workbook, {
                                    bookType: 'xlsx',
                                    type: 'array'
                                });
                                this.saveAsExcelFile(excelBuffer, 'Reports');
                                this.sendProgressDoneSignal();
                            });
                        } else {
                            this.customerSupportActivation = [];
                            this.sendProgressDoneSignal();
                            this.isDataAvailable = true;
                            data.results.data.forEach(x => {
                                while (x.beams.includes('<->')) {
                                    x.beams = x.beams.replace('<->', ', ');
                                }
                            });
                            this.customerSupportActivation = data.results.data.map((item, index) => ({ index, ...item }));
                            this.totalRecords = data.results.total;
                        }
                        /** Export as Excel ends */
                    } else {
                        this.customerSupportActivation = [];
                        this.norecordsFound = true;
                        this.sendProgressDoneSignal();
                    }
                },
                error => {
                    this.apiResponsePopup(error.error.responseMessage!, error.responseCode!);
                    this.sendProgressDoneSignal();
                    this.previousData.endDate = '';
                    this.previousData.partnerId = '';
                    this.filterDataForm.patchValue({
                        endDate: '',
                        partnerId: ''
                    });
                });
        }
    }

    public onChangeReport(event : { value : { name : {}}}) : void {
        this.filterDataForm.patchValue({
            // startDate: '',
            // endDate: '',
            partnerId: null
        });
        this.showMessage = '';
        if (event.value.name !== '' || event.value.name !== undefined || event.value.name !== null) {
            this.filterDataForm.controls.partnerId.enable();
        }
        this.clearData();
    }

    private apiResponsePopup(responseMsg: string, responseCode: number) :void {
        if (responseCode === 0) {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: responseMsg });
        } else {
            this.messageService.add({ severity: 'error', summary: 'Fail', detail: responseMsg });
        }
    }

    public saveAsExcelFile(buffer: any, fileName: string): void {
        const EXCEL_TYPE =
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const EXCEL_EXTENSION = '.xlsx';
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE
        });
        FileSaver.saveAs(
            data,
            fileName + '_' + moment().utc().format('MMMM Do YYYY HH:mm:ss') + EXCEL_EXTENSION
        );
    }

    public exportExcel() : void {
        const exportRequest : TestActivationRequestDTO = {
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            filter: {
                startDate: moment(this.filterDataForm.value.startDate).format('YYYY-MM-DD'),
                endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value.endDate).format('YYYY-MM-DD') : '',
                eventType: this.filterDataForm.value.eventType?.name,
                eventAction: this.filterDataForm.value.action?.name,
                status: this.filterDataForm.value.status?.value,
                partnerId: this.filterDataForm.value?.partnerId ? this.filterDataForm.value.partnerId : ''
            }
        };
        this.postTestActivation(exportRequest, true);
    }

    public checkfieldValues() : void {
        const previousDataCheck = {
            endDate: this.previousData?.endDate,
            partnerId: this.previousData?.partnerId
        };

        let currentDataCheck = {
            endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value?.endDate).format('YYYY-MM-DD') : '',
            partnerId: this.filterDataForm.value?.partnerId ? this.filterDataForm.value?.partnerId : ''
        };

        if (JSON.stringify(previousDataCheck) !== JSON.stringify(currentDataCheck)) {
            this.confirmationService.confirm({
                message: 'Data has been changed. Do you want to continue with new request ?',
                header: 'Confirmation',
                icon: 'pi pi-info-circle',
                acceptLabel: 'Yes',
                acceptVisible: true,
                rejectLabel: 'Cancel',
                rejectVisible: true,
                accept: () => {
                    this.submitData();
                },
                reject: () => {
                    this.filterDataForm.patchValue({
                        endDate: this.previousData?.endDate ? new Date(this.previousData?.endDate) : null,
                        partnerId: this.previousData?.partnerId ? this.previousData?.partnerId : null
                    });
                    this.onCancelSendData();
                    currentDataCheck = JSON.parse(JSON.stringify(previousDataCheck));
                }
            });
        } else {
            this.submitData();
        }
    }

    public onCancelSendData() : void {
        const configurationRequestBody : TestActivationRequestDTO = {
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            page: this.pageNumber,
            row: this.rows,
            filter: this.previousData!
        };
        this.postTestActivation(configurationRequestBody, false);
    }

    public onLazyLoad(event:LazyLoadEvent) : void {
        this.pageNumber = event.first! + event.rows!;
        this.pageNumber /= event.rows!;
        this.rows = event.rows!;
        this.sortField = event?.sortField!;
        if (this.sortField !== undefined) {
            if (event.sortOrder === 1) {
                this.sortOrder = 'asc';
            } else if (event.sortOrder === -1) {
                this.sortOrder = 'desc';
            }
        } else {
            this.sortOrder = '';
        }
        this.checkfieldValues();
    }

    public validateStartDate(event : Date) : void {
        this.clearData();
        const tempDate = JSON.parse(JSON.stringify(event));
        const fiveMinutesIntoFuture = new Date(tempDate);
        fiveMinutesIntoFuture.setMinutes(fiveMinutesIntoFuture.getMinutes());
        fiveMinutesIntoFuture.setFullYear(fiveMinutesIntoFuture.getFullYear() + 3);
        fiveMinutesIntoFuture.setDate(fiveMinutesIntoFuture.getDate() - 1);

        if (fiveMinutesIntoFuture >= new Date()) {
            this.maxScheduledEndDate = new Date();
        } else {
            this.maxScheduledEndDate = fiveMinutesIntoFuture;
        }

        this.showMessage = '';
        if (this.filterDataForm.value.endDate !== '' && this.filterDataForm.value.endDate !== null) {
            if (event > this.filterDataForm.value.endDate) {
                this.showMessage = 'Start Date should not be greater than End Date';
            } else if (event.getDate() === this.filterDataForm.value.endDate.getDate() &&
                        event.getMonth() === this.filterDataForm.value.endDate.getMonth() &&
                        event.getFullYear() === this.filterDataForm.value.endDate.getFullYear()) {
                this.showMessage = 'Start Date should not be equal to End Date';
            }
        }
    }

    public validateEndDate(event : Date) : void {
        // this.clearData();
        this.showMessage = '';
        if (this.filterDataForm.value.startDate !== '') {
            if (event < this.filterDataForm.value.startDate) {
                this.showMessage = 'End Date should not be less than Start date';
            } else if ((event.getDate() === this.filterDataForm.value.startDate?.getDate()) &&
                       (event.getMonth() === this.filterDataForm.value.startDate?.getMonth()) &&
                       (event.getFullYear() === this.filterDataForm.value.startDate?.getFullYear())) {
                this.showMessage = 'End Date should not be equal to Start Date';
            }
        }
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
        this.pageNumber = 1;
        this.rows = 10;
    }
}
